import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import MediaBrowser from '../components/MediaBrowser';

const ProductFormFinal = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMediaBrowser, setShowMediaBrowser] = useState(false);
  const [showVariantImageSelector, setShowVariantImageSelector] = useState(false);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);
  
  // Refs for scroll navigation
  const basicRef = useRef(null);
  const pricingRef = useRef(null);
  const inventoryRef = useRef(null);
  const variantsRef = useRef(null);
  const imagesRef = useRef(null);
  const settingsRef = useRef(null);
  const seoRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    description: '',
    product_type: 'PHYSICAL',
    available_in_store: true,
    
    // Pricing - Will be hidden if variants exist
    prices: [{
      name: 'Default Price',
      amount: 0,
      type: 'one_time',
      description: ''
    }],
    compare_at_price: null,
    sku: '',
    statement_descriptor: '',
    
    // Inventory
    track_inventory: false,
    continue_selling_when_out_of_stock: false,
    available_quantity: null,
    
    // Variants - GHL style with options
    variant_options: [], // [{name: "Color", values: ["Red", "Green"]}]
    variants: [], // Generated variants with pricing
    
    // Images
    images: [],
    featured_image_index: 0, // Index of the featured/default image
    
    // Categories
    categories: [],
    
    // Labels
    label: null,
    label_start_date: null,
    label_end_date: null,
    
    // Visibility
    visibility_start_date: null,
    visibility_end_date: null,
    
    // Tax
    is_taxes_enabled: false,
    tax_inclusive: false,
    taxes: [],
    
    // SEO
    seo_title: '',
    seo_description: '',
    handle: '',
    
    // Advanced
    custom_attributes: {}
  });

  useEffect(() => {
    fetchCategories();
    if (id) {
      fetchProduct();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories');
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await axiosInstance.get(`/products/${id}`);
      const product = response.data;
      
      const cleanDescription = product.description?.replace(/<[^>]*>/g, '') || '';
      
      // Handle pricing - use prices array if available, otherwise create from product.price
      console.log('[DEBUG] Product data:', { 
        price: product.price, 
        prices: product.prices,
        product_type: product.product_type 
      });
      
      const pricesArray = product.prices?.length > 0 
        ? product.prices 
        : [{
            name: 'Default Price',
            amount: product.price ? parseFloat(product.price) : 0,
            type: 'one_time',
            description: ''
          }];
      
      console.log('[DEBUG] Prices array after processing:', pricesArray);
      console.log('[DEBUG] First price amount:', pricesArray[0]?.amount);
      
      setFormData({
        name: product.name || '',
        description: cleanDescription,
        product_type: product.product_type || 'PHYSICAL',
        available_in_store: product.available_in_store !== false,
        prices: pricesArray,
        compare_at_price: product.compare_at_price || null,
        sku: product.sku || '',
        statement_descriptor: product.statement_descriptor || '',
        track_inventory: product.track_inventory || false,
        continue_selling_when_out_of_stock: product.continue_selling_when_out_of_stock || false,
        available_quantity: product.available_quantity || null,
        variant_options: product.variant_options || [],
        variants: product.variants || [],
        images: product.images || [],
        featured_image_index: product.featured_image_index || 0,
        categories: product.categories || [],
        label: product.label || null,
        label_start_date: product.label_start_date || null,
        label_end_date: product.label_end_date || null,
        visibility_start_date: product.visibility_start_date || null,
        visibility_end_date: product.visibility_end_date || null,
        is_taxes_enabled: product.is_taxes_enabled || false,
        tax_inclusive: product.tax_inclusive || false,
        taxes: product.taxes || [],
        seo_title: product.seo_title || '',
        seo_description: product.seo_description || '',
        handle: product.handle || product.seo_url || '',
        custom_attributes: product.custom_attributes || {}
      });
    } catch (error) {
      toast.error('Failed to load product');
    }
  };

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Variant Option Management
  const addVariantOption = () => {
    setFormData(prev => ({
      ...prev,
      variant_options: [...prev.variant_options, { name: '', values: [''] }]
    }));
  };

  const removeVariantOption = (index) => {
    setFormData(prev => ({
      ...prev,
      variant_options: prev.variant_options.filter((_, i) => i !== index)
    }));
    generateVariants();
  };

  const updateVariantOptionName = (index, name) => {
    setFormData(prev => {
      const newOptions = [...prev.variant_options];
      newOptions[index].name = name;
      return { ...prev, variant_options: newOptions };
    });
  };

  const addVariantOptionValue = (optionIndex) => {
    setFormData(prev => {
      const newOptions = [...prev.variant_options];
      newOptions[optionIndex].values.push('');
      return { ...prev, variant_options: newOptions };
    });
  };

  const updateVariantOptionValue = (optionIndex, valueIndex, value) => {
    setFormData(prev => {
      const newOptions = [...prev.variant_options];
      newOptions[optionIndex].values[valueIndex] = value;
      return { ...prev, variant_options: newOptions };
    });
  };

  const removeVariantOptionValue = (optionIndex, valueIndex) => {
    setFormData(prev => {
      const newOptions = [...prev.variant_options];
      newOptions[optionIndex].values = newOptions[optionIndex].values.filter((_, i) => i !== valueIndex);
      return { ...prev, variant_options: newOptions };
    });
    generateVariants();
  };

  const generateVariants = () => {
    // Generate all possible combinations of variant options
    const options = formData.variant_options.filter(opt => opt.name && opt.values.some(v => v));
    
    if (options.length === 0) {
      setFormData(prev => ({ ...prev, variants: [] }));
      return;
    }

    const combinations = [];
    const generate = (current, depth) => {
      if (depth === options.length) {
        combinations.push(current);
        return;
      }
      
      const option = options[depth];
      option.values.filter(v => v).forEach(value => {
        generate([...current, value], depth + 1);
      });
    };

    generate([], 0);

    const newVariants = combinations.map(combo => {
      const variantName = combo.join(' / ');
      const existing = formData.variants.find(v => v.name === variantName);
      
      return existing || {
        name: variantName,
        price: 0,
        compare_at_price: null,
        available_quantity: 0
      };
    });

    setFormData(prev => ({ ...prev, variants: newVariants }));
  };

  const updateVariant = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => 
        i === index ? { ...v, [field]: value } : v
      )
    }));
  };


  const getAllParentCategories = (categoryId) => {
    const parents = [];
    let currentCategory = categories.find(cat => cat.id === categoryId);
    
    while (currentCategory && currentCategory.parent_id) {
      parents.push(currentCategory.parent_id);
      currentCategory = categories.find(cat => cat.id === currentCategory.parent_id);
    }
    
    return parents;
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => {
      const isCurrentlySelected = prev.categories.includes(categoryId);
      
      if (isCurrentlySelected) {
        // When unchecking, just remove this category
        return {
          ...prev,
          categories: prev.categories.filter(id => id !== categoryId)
        };
      } else {
        // When checking, add this category AND all its parents
        const parentCategories = getAllParentCategories(categoryId);
        const allCategoriesToAdd = [categoryId, ...parentCategories];
        
        // Combine with existing categories and remove duplicates
        const updatedCategories = [...new Set([...prev.categories, ...allCategoriesToAdd])];
        
        return {
          ...prev,
          categories: updatedCategories
        };
      }
    });
  };

  const handleMediaSelect = (urls) => {
    const urlArray = Array.isArray(urls) ? urls : [urls];
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...urlArray]
    }));
    toast.success(`${urlArray.length} image(s) added`);
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formDataUpload = new FormData();
    for (let file of files) {
      formDataUpload.append('files', file);
    }

    try {
      toast.info('Uploading to GoHighLevel...');
      
      const response = await axiosInstance.post('/media/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Extract URLs from response.data.results array
      const imageUrls = response.data.results
        .filter(r => r.url) // Only include successful uploads
        .map(r => r.url);
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...imageUrls]
      }));
      
      const { uploaded, existing, failed } = response.data;
      
      if (failed > 0) {
        toast.warning(`Uploaded ${uploaded}, reused ${existing} existing, ${failed} failed`);
      } else if (existing > 0) {
        toast.success(`Uploaded ${uploaded} new, reused ${existing} existing images`);
      } else {
        toast.success(`Successfully uploaded ${uploaded} image(s) to GoHighLevel`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload images to GHL');
    }
  };

  const handleImageRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      featured_image_index: prev.featured_image_index === index ? 0 : 
        (prev.featured_image_index > index ? prev.featured_image_index - 1 : prev.featured_image_index)
    }));
  };

  const setFeaturedImage = (index) => {
    setFormData(prev => ({
      ...prev,
      featured_image_index: index
    }));
  };

  const openVariantImageSelector = (variantIndex) => {
    setSelectedVariantIndex(variantIndex);
    setShowVariantImageSelector(true);
  };

  const selectVariantImage = (imageUrl) => {
    if (selectedVariantIndex !== null) {
      updateVariant(selectedVariantIndex, 'image', imageUrl);
      setShowVariantImageSelector(false);
      setSelectedVariantIndex(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = { 
        ...formData,
        price: formData.prices[0]?.amount || 0
      };
      
      if (!payload.compare_at_price) payload.compare_at_price = null;
      if (!payload.visibility_start_date) payload.visibility_start_date = null;
      if (!payload.visibility_end_date) payload.visibility_end_date = null;
      if (!payload.label?.title) payload.label = null;

      if (id) {
        const response = await axiosInstance.put(`/products/${id}`, payload);
        toast.success('Product updated successfully');
        
        // Show sync status if available
        const syncStatus = response.data.sync_status;
        if (syncStatus) {
          if (syncStatus.synced) {
            toast.success('✓ Synced to Excalibur', { duration: 3000 });
          } else if (syncStatus.message && syncStatus.message !== "GHL not connected") {
            toast.warning(syncStatus.message);
          }
        }
      } else {
        await axiosInstance.post('/products', payload);
        toast.success('Product created successfully');
      }
      navigate('/products');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const renderCategoryTree = (parentId = null, level = 0) => {
    return categories
      .filter(cat => cat.parent_id === parentId)
      .map(category => (
        <div key={category.id} style={{ marginLeft: level * 20 }}>
          <label className="flex items-center space-x-2 py-1 cursor-pointer hover:bg-slate-50 px-2 rounded">
            <input
              type="checkbox"
              checked={formData.categories.includes(category.id)}
              onChange={() => handleCategoryToggle(category.id)}
              className="rounded border-slate-300"
            />
            <span className="text-sm">{category.name}</span>
          </label>
          {renderCategoryTree(category.id, level + 1)}
        </div>
      ));
  };

  const hasVariants = formData.variant_options.some(opt => opt.name && opt.values.some(v => v));

  const navItems = [
    { label: 'Basic Info', ref: basicRef },
    { label: 'Pricing', ref: pricingRef, hide: hasVariants },
    { label: 'Inventory', ref: inventoryRef },
    { label: 'Variants', ref: variantsRef },
    { label: 'Images', ref: imagesRef },
    { label: 'Settings', ref: settingsRef },
    { label: 'SEO', ref: seoRef }
  ];

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Vertical Navigation Sidebar */}
      <div className="w-64 bg-white border-r border-slate-200 p-6 sticky top-0 h-screen overflow-y-auto">
        <h2 className="text-xl font-bold text-slate-800 mb-6">
          {id ? 'Edit Product' : 'New Product'}
        </h2>
        <nav className="space-y-2">
          {navItems.filter(item => !item.hide).map((item, index) => (
            <button
              key={index}
              type="button"
              onClick={() => scrollToSection(item.ref)}
              className="w-full text-left px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
            >
              {item.label}
            </button>
          ))}
        </nav>
        
        <div className="mt-8 pt-6 border-t space-y-2">
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="w-full"
          >
            {loading ? 'Saving...' : (id ? 'Update' : 'Create')}
          </Button>
          <Button 
            onClick={() => navigate('/products')} 
            variant="outline"
            className="w-full"
          >
            Cancel
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8 overflow-y-auto">
        <form onSubmit={handleSubmit} className="max-w-4xl space-y-8">
          
          {/* Basic Info Section */}
          <section ref={basicRef} className="bg-white p-6 rounded-lg border space-y-6">
            <h3 className="text-2xl font-bold text-slate-800 mb-4">Basic Information</h3>
            
            <div>
              <Label htmlFor="name">Product Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter product name"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={6}
                placeholder="Enter product description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="product_type">Product Type</Label>
                <select
                  id="product_type"
                  name="product_type"
                  value={formData.product_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md"
                >
                  <option value="PHYSICAL">Physical</option>
                  <option value="DIGITAL">Digital</option>
                </select>
              </div>

              <div className="flex items-center space-x-2 pt-8">
                <input
                  type="checkbox"
                  id="available_in_store"
                  name="available_in_store"
                  checked={formData.available_in_store}
                  onChange={handleChange}
                  className="rounded border-slate-300"
                />
                <Label htmlFor="available_in_store" className="cursor-pointer">
                  Available in Store
                </Label>
              </div>
            </div>

            <div>
              <Label>Collections (Categories)</Label>
              <div className="mt-2 border border-slate-300 rounded-md p-4 max-h-64 overflow-y-auto bg-slate-50">
                {renderCategoryTree()}
              </div>
            </div>

            <div>
              <Label>Product Label (Optional)</Label>
              <div className="space-y-3 mt-2">
                <Input
                  placeholder="Label Text (e.g., IN-STORE ONLY)"
                  value={formData.label?.title || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    label: { ...prev.label, title: e.target.value }
                  }))}
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Label Start Date</Label>
                    <Input
                      type="datetime-local"
                      value={formData.label_start_date || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        label_start_date: e.target.value
                      }))}
                    />
                  </div>
                  <div>
                    <Label>Label End Date</Label>
                    <Input
                      type="datetime-local"
                      value={formData.label_end_date || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        label_end_date: e.target.value
                      }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Pricing Section - Completely removed when variants exist */}
          {!hasVariants && (
            <section ref={pricingRef} className="bg-white p-6 rounded-lg border space-y-6">
              <h3 className="text-2xl font-bold text-slate-800 mb-4">Pricing</h3>
              
              <div>
                <Label>Price *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.prices[0]?.amount || 0}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    prices: [{ ...prev.prices[0], amount: parseFloat(e.target.value) }]
                  }))}
                  required
                />
              </div>

              <div>
                <Label>Compare At Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.compare_at_price || ''}
                  onChange={handleChange}
                  name="compare_at_price"
                  placeholder="Original price"
                />
              </div>

              <div>
                <Label htmlFor="sku">SKU</Label>
                <Input
                  id="sku"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  placeholder="Product SKU"
                />
              </div>

              <div>
                <Label htmlFor="statement_descriptor">Statement Descriptor</Label>
                <Input
                  id="statement_descriptor"
                  name="statement_descriptor"
                  value={formData.statement_descriptor}
                  onChange={handleChange}
                  placeholder="Appears on customer's credit card statement"
                  maxLength={22}
                />
              </div>
            </section>
          )}

          {/* Inventory Section */}
          <section ref={inventoryRef} className="bg-white p-6 rounded-lg border space-y-6">
            <h3 className="text-2xl font-bold text-slate-800 mb-4">Inventory</h3>
            
            <div className="space-y-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="track_inventory"
                  name="track_inventory"
                  checked={formData.track_inventory}
                  onChange={handleChange}
                  className="rounded border-slate-300"
                />
                <span>Track Inventory</span>
              </label>

              {formData.track_inventory && (
                <>
                  <label className="flex items-center space-x-2 ml-6">
                    <input
                      type="checkbox"
                      id="continue_selling_when_out_of_stock"
                      name="continue_selling_when_out_of_stock"
                      checked={formData.continue_selling_when_out_of_stock}
                      onChange={handleChange}
                      className="rounded border-slate-300"
                    />
                    <span>Continue selling when out of stock</span>
                  </label>

                  {!hasVariants && (
                    <div className="ml-6">
                      <Label htmlFor="available_quantity">Available Quantity</Label>
                      <Input
                        id="available_quantity"
                        name="available_quantity"
                        type="number"
                        value={formData.available_quantity || ''}
                        onChange={handleChange}
                        placeholder="Enter available quantity"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* Variants Section */}
          <section ref={variantsRef} className="bg-white p-6 rounded-lg border space-y-6">
            <h3 className="text-2xl font-bold text-slate-800 mb-4">Variants</h3>
            <p className="text-sm text-slate-500">Add options like size or color</p>
            
            {formData.variants.length > 0 && formData.images.length === 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <span className="text-2xl">💡</span>
                <div>
                  <p className="font-medium text-blue-900">Tip: Upload product images first</p>
                  <p className="text-sm text-blue-700">
                    To assign images to variants, scroll down to the Images section and upload your product photos first. 
                    Then you can select which image appears for each variant.
                  </p>
                </div>
              </div>
            )}
            
            {/* Variant Options */}
            {formData.variant_options.map((option, optionIndex) => (
              <div key={optionIndex} className="border border-slate-200 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Option name *</Label>
                  <button
                    type="button"
                    onClick={() => removeVariantOption(optionIndex)}
                    className="text-red-500 hover:text-red-700"
                  >
                    🗑️
                  </button>
                </div>
                
                <Input
                  value={option.name}
                  onChange={(e) => updateVariantOptionName(optionIndex, e.target.value)}
                  placeholder="e.g., Color"
                />

                <div>
                  <Label>Option values *</Label>
                  <div className="space-y-2 mt-2">
                    {option.values.map((value, valueIndex) => (
                      <div key={valueIndex} className="flex items-center gap-2">
                        <Input
                          value={value}
                          onChange={(e) => updateVariantOptionValue(optionIndex, valueIndex, e.target.value)}
                          placeholder="e.g., Red"
                        />
                        <button
                          type="button"
                          onClick={() => removeVariantOptionValue(optionIndex, valueIndex)}
                          className="text-red-500 hover:text-red-700"
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                    <Input
                      placeholder="Add another value"
                      onFocus={() => addVariantOptionValue(optionIndex)}
                    />
                  </div>
                </div>

                <Button 
                  type="button" 
                  onClick={generateVariants}
                  size="sm"
                >
                  Done
                </Button>
              </div>
            ))}

            <Button type="button" variant="outline" onClick={addVariantOption}>
              + Add another variant
            </Button>

            {/* Generated Variants Table */}
            {formData.variants.length > 0 && (
              <div className="mt-6">
                <h4 className="font-semibold mb-4">Variant Configuration</h4>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 text-sm font-medium">Variant name</th>
                        <th className="text-left p-3 text-sm font-medium">Image</th>
                        <th className="text-left p-3 text-sm font-medium">Price</th>
                        <th className="text-left p-3 text-sm font-medium">Compare-at price</th>
                        {formData.track_inventory && (
                          <th className="text-left p-3 text-sm font-medium">Available QTY</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {formData.variants.map((variant, index) => (
                        <tr key={index} className="border-b">
                          <td className="p-3">
                            <span className="font-medium">{variant.name}</span>
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">New</span>
                          </td>
                          <td className="p-3">
                            {formData.images.length > 0 ? (
                              <div className="flex items-center gap-2">
                                {/* Thumbnail preview */}
                                {variant.image ? (
                                  <img 
                                    src={variant.image} 
                                    alt="Variant" 
                                    className="w-16 h-16 object-cover rounded border-2 border-slate-300"
                                  />
                                ) : (
                                  <div className="w-16 h-16 bg-slate-100 rounded border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-xs">
                                    No image
                                  </div>
                                )}
                                
                                {/* Select Image Button */}
                                <Button
                                  type="button"
                                  onClick={() => openVariantImageSelector(index)}
                                  variant="outline"
                                  size="sm"
                                  className="text-xs"
                                >
                                  {variant.image ? 'Change' : 'Select'} Image
                                </Button>
                                
                                {/* Clear button if image is selected */}
                                {variant.image && (
                                  <button
                                    type="button"
                                    onClick={() => updateVariant(index, 'image', null)}
                                    className="text-red-500 hover:text-red-700 text-xs"
                                    title="Remove image"
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">Upload images first</span>
                            )}
                          </td>
                          <td className="p-3">
                            <Input
                              type="number"
                              step="0.01"
                              value={variant.price}
                              onChange={(e) => updateVariant(index, 'price', parseFloat(e.target.value))}
                              placeholder="Amount"
                              className="w-32"
                            />
                          </td>
                          <td className="p-3">
                            <Input
                              type="number"
                              step="0.01"
                              value={variant.compare_at_price || ''}
                              onChange={(e) => updateVariant(index, 'compare_at_price', parseFloat(e.target.value))}
                              placeholder="Compare-at"
                              className="w-32"
                            />
                          </td>
                          {formData.track_inventory && (
                            <td className="p-3">
                              <Input
                                type="number"
                                value={variant.available_quantity || 0}
                                onChange={(e) => updateVariant(index, 'available_quantity', parseInt(e.target.value))}
                                className="w-24"
                              />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          {/* Images Section */}
          <section ref={imagesRef} className="bg-white p-6 rounded-lg border space-y-6">
            <h3 className="text-2xl font-bold text-slate-800 mb-4">Images</h3>
            
            <div>
              <Label>Product Images</Label>
              <p className="text-sm text-slate-500 mb-4">Add images from Excalibur media storage</p>
              
              <div className="mb-4">
                <Button 
                  type="button" 
                  onClick={() => setShowMediaBrowser(true)}
                  className="w-full"
                >
                  📁 Choose from Media
                </Button>
              </div>

              <div className="grid grid-cols-4 gap-4">
                {formData.images.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={url}
                      alt={`Product ${index + 1}`}
                      className={`w-full h-32 object-cover rounded-lg border-2 transition ${
                        formData.featured_image_index === index 
                          ? 'border-yellow-400 ring-2 ring-yellow-200' 
                          : 'border-slate-200'
                      }`}
                    />
                    
                    {/* Featured/Default star indicator */}
                    {formData.featured_image_index === index && (
                      <div className="absolute top-2 left-2 bg-yellow-400 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-lg">
                        ⭐
                      </div>
                    )}
                    
                    {/* Set as default button */}
                    {formData.featured_image_index !== index && (
                      <button
                        type="button"
                        onClick={() => setFeaturedImage(index)}
                        className="absolute top-2 left-2 bg-slate-700 text-white rounded-full w-7 h-7 flex items-center justify-center opacity-0 group-hover:opacity-90 transition text-xs"
                        title="Set as default image"
                      >
                        ☆
                      </button>
                    )}
                    
                    {/* Remove button */}
                    <button
                      type="button"
                      onClick={() => handleImageRemove(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    >
                      ✕
                    </button>
                    
                    {/* Image number label */}
                    <div className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                      Image {index + 1}
                    </div>
                  </div>
                ))}
              </div>

              {formData.images.length === 0 && (
                <div className="text-center py-12 border-2 border-dashed rounded-lg border-slate-300 text-slate-400">
                  No images added yet
                </div>
              )}
            </div>
          </section>

          {/* Settings Section */}
          <section ref={settingsRef} className="bg-white p-6 rounded-lg border space-y-6">
            <h3 className="text-2xl font-bold text-slate-800 mb-4">Settings</h3>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">Tax Settings</h4>
              <div className="space-y-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="is_taxes_enabled"
                    checked={formData.is_taxes_enabled}
                    onChange={handleChange}
                    className="rounded border-slate-300"
                  />
                  <span>Enable Taxes</span>
                </label>

                {formData.is_taxes_enabled && (
                  <label className="flex items-center space-x-2 ml-6">
                    <input
                      type="checkbox"
                      name="tax_inclusive"
                      checked={formData.tax_inclusive}
                      onChange={handleChange}
                      className="rounded border-slate-300"
                    />
                    <span>Tax Inclusive Pricing</span>
                  </label>
                )}
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-lg mb-4">Visibility Duration</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="visibility_start_date">Start Date</Label>
                  <Input
                    id="visibility_start_date"
                    name="visibility_start_date"
                    type="datetime-local"
                    value={formData.visibility_start_date || ''}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <Label htmlFor="visibility_end_date">End Date</Label>
                  <Input
                    id="visibility_end_date"
                    name="visibility_end_date"
                    type="datetime-local"
                    value={formData.visibility_end_date || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* SEO Section */}
          <section ref={seoRef} className="bg-white p-6 rounded-lg border space-y-6">
            <h3 className="text-2xl font-bold text-slate-800 mb-4">SEO</h3>
            
            <div>
              <Label htmlFor="seo_title">SEO Title</Label>
              <Input
                id="seo_title"
                name="seo_title"
                value={formData.seo_title}
                onChange={handleChange}
                placeholder="Product name for search engines"
                maxLength={60}
              />
            </div>

            <div>
              <Label htmlFor="seo_description">SEO Description</Label>
              <Textarea
                id="seo_description"
                name="seo_description"
                value={formData.seo_description}
                onChange={handleChange}
                rows={3}
                placeholder="Product description for search engines"
                maxLength={160}
              />
            </div>

            <div>
              <Label htmlFor="handle">Handle</Label>
              <Input
                id="handle"
                name="handle"
                value={formData.handle}
                onChange={handleChange}
                placeholder="product-url-handle"
              />
              <p className="text-xs text-slate-500 mt-1">Use lowercase and hyphens</p>
            </div>
          </section>
        </form>

        {/* Media Browser Modal */}
        <MediaBrowser
          isOpen={showMediaBrowser}
          onClose={() => setShowMediaBrowser(false)}
          onSelect={handleMediaSelect}
          allowMultiple={true}
        />

        {/* Variant Image Selector Modal */}
        {showVariantImageSelector && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">Select Variant Image</h3>
                  {selectedVariantIndex !== null && (
                    <p className="text-indigo-100 text-sm mt-1">
                      For: {formData.variants[selectedVariantIndex]?.name}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowVariantImageSelector(false);
                    setSelectedVariantIndex(null);
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full w-10 h-10 flex items-center justify-center transition"
                >
                  ✕
                </button>
              </div>

              {/* Image Grid */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                <div className="grid grid-cols-3 gap-4">
                  {formData.images.map((imageUrl, imgIndex) => {
                    const isSelected = selectedVariantIndex !== null && 
                      formData.variants[selectedVariantIndex]?.image === imageUrl;
                    const isFeatured = formData.featured_image_index === imgIndex;
                    
                    return (
                      <button
                        key={imgIndex}
                        type="button"
                        onClick={() => selectVariantImage(imageUrl)}
                        className={`relative border-3 rounded-lg overflow-hidden hover:scale-105 transition-transform ${
                          isSelected 
                            ? 'border-indigo-600 ring-4 ring-indigo-300' 
                            : 'border-slate-300 hover:border-indigo-400'
                        }`}
                      >
                        <img 
                          src={imageUrl} 
                          alt={`Image ${imgIndex + 1}`}
                          className="w-full h-48 object-cover"
                        />
                        
                        {/* Selected checkmark */}
                        {isSelected && (
                          <div className="absolute top-3 right-3 bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg">
                            ✓
                          </div>
                        )}
                        
                        {/* Featured star */}
                        {isFeatured && (
                          <div className="absolute top-3 left-3 bg-yellow-400 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-lg">
                            ⭐
                          </div>
                        )}
                        
                        {/* Image label */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent text-white p-3">
                          <p className="font-medium text-sm">Image {imgIndex + 1}</p>
                          {isFeatured && (
                            <p className="text-xs text-yellow-300">Default Product Image</p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {formData.images.length === 0 && (
                  <div className="text-center py-12 text-slate-400">
                    <p className="text-lg mb-2">No images available</p>
                    <p className="text-sm">Upload product images first in the Images section</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-slate-50 px-6 py-4 border-t flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => {
                    if (selectedVariantIndex !== null) {
                      updateVariant(selectedVariantIndex, 'image', null);
                      setShowVariantImageSelector(false);
                      setSelectedVariantIndex(null);
                    }
                  }}
                  className="text-red-600 hover:text-red-700 font-medium text-sm"
                >
                  Clear Selection
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowVariantImageSelector(false);
                    setSelectedVariantIndex(null);
                  }}
                  className="px-6 py-2 bg-slate-200 hover:bg-slate-300 rounded-lg font-medium transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductFormFinal;
